import { createScriptLoader } from '@shared/util/dom';
import { getConfigString } from '@shared/util/env';
import { CommandName, ZendeskMixin } from './types';

export const BASE_URL = 'https://skilllabdesk.zendesk.com';
export const API_URL = `${BASE_URL}/api/v2`;
const zendeskyWindow = window as typeof window & ZendeskMixin;
zendeskyWindow.zESettings = {
  webWidget: {
    color: {
      theme: '#2069b2',
      launcher: '#030F4A',
    },
  },
};

const listeners = {
  open: [] as Array<() => void>,
  close: [] as Array<() => void>,
  'chat:unreadMessages': [] as Array<(num: number) => void>,
} as const;
type Listeners = typeof listeners;
type EventName = keyof typeof listeners;

export const addEventListener = <T extends EventName>(event: T, listener: Listeners[T][number]) => {
  listeners[event].push(listener as any);
};

export const removeEventListener = <T extends EventName>(
  event: T,
  listener: Listeners[T][number],
) => {
  const index = listeners[event].findIndex((i) => i === listener);
  if (index >= 0) {
    listeners[event].splice(index, 1);
  }
};

const loader = createScriptLoader('ze-snippet');

let scriptLoaded = false;
let scriptFailed = false;
const loadScript = () => {
  const key = getConfigString('zendesk-id');
  if (!key) {
    scriptFailed = true;
    return Promise.reject(false);
  }
  const src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;
  return loader(src).then(
    () => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      scriptLoaded = zendeskyWindow.zE !== undefined;
      if (!scriptLoaded) {
        return;
      }
      (Object.keys(listeners) as EventName[]).forEach((event) => {
        zendeskyWindow.zE('webWidget:on', event as any, (...params: any[]) => {
          listeners[event].forEach((listener: any) => listener(...params));
        });
      });
    },
    () => (scriptFailed = true),
  );
};

const handleError = (_: any) => void 0;

export const executeCommand = (command: CommandName, options?: any) => {
  if (scriptFailed) {
    // silent for now
    return;
  }
  const executor = () => zendeskyWindow.zE('webWidget', command as any, options);
  if (scriptLoaded) {
    try {
      executor();
    } catch (ex) {
      handleError(ex);
    }
  } else {
    loadScript().then(executor, handleError);
  }
};

export const getCurrentWidgetScreen = () => zendeskyWindow.zE('webWidget:get', 'display');
